import * as Service from 'types/service';
import { ClientBusinessDefinedState, dashboard } from 'types';
import { ServiceDefinedState } from 'state/service/config';
import { concat, find, findIndex, forEach, reduce, size } from 'es-toolkit/compat';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { getPartyStatusColorClassNames } from 'constants/partyColorConstants';
import { safeEnumCompare } from '@tock/coretypes';
import protos from 'utils/protos';

const { PartyState } = protos;

// Built-in system party statuses whose label can't be edited (color/icon/ordering stay editable).
export const NON_EDITABLE_PARTY_STATUSES: ReadonlySet<dashboard.PartyState> = new Set([
  'EXPECTED',
  'PARTIALLY_ARRIVED',
  'ARRIVED',
  'PARTIALLY_SEATED',
  'SEATED',
  'LEFT',
  'NO_SHOW',
  'CANCELED',
] as dashboard.PartyState[]);

// A party status label is locked when it's a built-in system status (not deletable, known primary state).
export function isPartyStatusNameLocked(state?: ClientBusinessDefinedState): boolean {
  return (
    !!state && state.canDelete === false && NON_EDITABLE_PARTY_STATUSES.has(state.primaryState)
  );
}

/**
 * Returns the display name for a primary party-state enum in sentence case.
 * LEFT keeps its established product-facing name, "Finished".
 */
export function getSentenceCasePartyStateName(
  primaryState: Service.PartyState | dashboard.PartyState
): string {
  if (primaryState === 'LEFT') {
    return 'Finished';
  }

  const sentenceCaseName = primaryState.toLocaleLowerCase().replace(/_/g, ' ');
  return sentenceCaseName.charAt(0).toUpperCase() + sentenceCaseName.slice(1);
}

export function convertClientObjectToServerObject(
  businessDefinedState: ClientBusinessDefinedState
): dashboard.BusinessDefinedState {
  // Destructure client-only fields so they don't leak into the server object.
  // All other fields (including any future proto additions) pass through via spread.
  const {
    className: _className,
    strokeColorClassName: _strokeColorClassName,
    key: _key,
    label: _label,
    ...serverFields
  } = businessDefinedState;
  return {
    ...serverFields,
    id: serverFields.id || 0,
    secondaryState: serverFields.secondaryState || '',
    pantryColor: serverFields.pantryColor ?? '',
    icon: serverFields.icon ?? '',
    border: serverFields.border ?? '',
  };
}

export function updateBusinessDefinedStatesLists(
  stateList: ClientBusinessDefinedState[],
  newState: ClientBusinessDefinedState
): ClientBusinessDefinedState[] {
  const replacingIndex = findIndex(
    stateList,
    (state: ClientBusinessDefinedState) => state.id === newState.id,
    0
  );

  if (replacingIndex >= 0 && stateList[replacingIndex].ordering !== newState.ordering) {
    // Reordering, need to update ordering of other objects
    // Currently we only swap, items when we switch to drag and drop, update this TODO - LCN 03/04/19
    let listToSearchIn;
    const listOptions = filterIntoExpectedAndSeated(stateList, true);
    if (getPartyStatusColumn(newState.primaryState) === 'seated') {
      listToSearchIn = listOptions.seated;
    } else {
      listToSearchIn = listOptions.expected;
    }
    // Need to find the item that has the old ordering to swap
    const stateInOldPosition = find(
      listToSearchIn,
      (state: ClientBusinessDefinedState) => state.ordering === newState.ordering
    );
    if (!stateInOldPosition) {
      // if you can't find the other state, change nothing
      return stateList;
    }
    const indexOfOldState = findIndex(
      stateList,
      (state: ClientBusinessDefinedState) => state.key === stateInOldPosition.key
    );
    stateList[indexOfOldState].ordering = stateList[replacingIndex].ordering;
  }
  stateList[replacingIndex] = newState;
  return stateList;
}

export function applySequentialOrdering<T extends { ordering: number }>(states: T[]): T[] {
  return states.map((state, ordering) => ({
    ...state,
    ordering,
  }));
}

interface HasPrimaryState {
  primaryState: Service.PartyState | string | number;
}

export function filterPartyStates<T extends HasPrimaryState>(
  states: T[]
): {
  [primaryState: string]: T[];
} {
  let expected: T[] = [];
  let seated: T[] = [];
  let noshow: T[] = [];
  let left: T[] = [];
  let canceled: T[] = [];
  forEach(states, (state) => {
    const primaryState = state.primaryState;
    if (safeEnumCompare(primaryState, PartyState, PartyState.SEATED)) {
      seated = concat(seated, state);
    } else if (
      safeEnumCompare(primaryState, PartyState, PartyState.ARRIVED) ||
      safeEnumCompare(primaryState, PartyState, PartyState.PARTIALLY_ARRIVED) ||
      safeEnumCompare(primaryState, PartyState, PartyState.PARTIALLY_SEATED) ||
      safeEnumCompare(primaryState, PartyState, PartyState.EXPECTED)
    ) {
      expected = concat(expected, state);
    } else if (safeEnumCompare(primaryState, PartyState, PartyState.LEFT)) {
      left = concat(left, state);
    } else if (safeEnumCompare(primaryState, PartyState, PartyState.NO_SHOW)) {
      noshow = concat(noshow, state);
    } else if (safeEnumCompare(primaryState, PartyState, PartyState.CANCELED)) {
      canceled = concat(canceled, state);
    }
  });
  return {
    expected,
    left,
    noshow,
    seated,
    canceled,
  };
}

export function filterIntoExpectedAndSeated<T extends HasPrimaryState>(
  states: T[],
  includeTerminalStates = false
) {
  const result = filterPartyStates(states);
  return {
    expected: includeTerminalStates ? concat(result.expected, result.noshow) : result.expected,
    seated: includeTerminalStates ? concat(result.seated, result.left) : result.seated,
  };
}

export type PartyStatusColumn = 'expected' | 'seated';

export function getPartyStatusColumn(
  primaryState: HasPrimaryState['primaryState']
): PartyStatusColumn | undefined {
  if (
    safeEnumCompare(primaryState, PartyState, PartyState.SEATED) ||
    safeEnumCompare(primaryState, PartyState, PartyState.LEFT)
  ) {
    return 'seated';
  }
  if (
    safeEnumCompare(primaryState, PartyState, PartyState.EXPECTED) ||
    safeEnumCompare(primaryState, PartyState, PartyState.ARRIVED) ||
    safeEnumCompare(primaryState, PartyState, PartyState.PARTIALLY_ARRIVED) ||
    safeEnumCompare(primaryState, PartyState, PartyState.PARTIALLY_SEATED) ||
    safeEnumCompare(primaryState, PartyState, PartyState.NO_SHOW)
  ) {
    return 'expected';
  }
  return undefined;
}

export function isServiceDefinedPartyState(item: string): item is Service.PartyState {
  return (
    item === 'EXPECTED' ||
    item === 'SEATED' ||
    item === 'LEFT' ||
    item === 'NO_SHOW' ||
    item === 'CANCELED' ||
    item === 'ARRIVED' ||
    item === 'PARTIALLY_ARRIVED' ||
    item === 'PARTIALLY_SEATED'
  );
}

export const createSecondaryStates = function createSecondaryStates(
  businessDefinedStates: ServiceDefinedState[],
  partyStatusRefreshEnabled: boolean
): Service.StateDescription[] {
  const stateDescriptions: Service.StateDescription[] = reduce(
    businessDefinedStates,
    (
      acc: Service.StateDescription[],
      businessDefinedState: ServiceDefinedState
    ): Service.StateDescription[] => {
      if (!isServiceDefinedPartyState(businessDefinedState.primaryState)) {
        return acc;
      }

      const classNamesForColor = getPartyStatusColorClassNames(
        businessDefinedState.color,
        businessDefinedState.pantryColor,
        partyStatusRefreshEnabled
      );
      const newBusinessDefinedState = {
        aerialTableBackgroundClassName: classNamesForColor.aerialTableBackground || '',
        aerialTableForegroundClassName: classNamesForColor.aerialTableForeground || '',
        border: businessDefinedState.border,
        borderClassName: classNamesForColor.borderColor || '',
        className: classNamesForColor.backgroundColor || '',
        icon: businessDefinedState.icon,
        ordering: businessDefinedState.ordering,
        primaryState: businessDefinedState.primaryState,
        secondaryValue: businessDefinedState.stateName,
        stateName: businessDefinedState.stateName,
        strokeColorClassName: classNamesForColor.strokeColor || '',
        textClassName: classNamesForColor.textColor || '',
        textFgColorClassName: classNamesForColor.textFgColor || '',
      };

      acc.push(newBusinessDefinedState);

      return acc;
    },
    []
  );

  if (size(stateDescriptions) === 0) {
    return [];
  }

  return stateDescriptions
    .filter(filterNilValues)
    .filter((state: Service.StateDescription) => !!(state.stateName && state.className))
    .filter((state: Service.StateDescription) => state.stateName !== 'Canceled');
};
